import { useEffect, useState } from 'react';
import type { NudgeReason } from '@gwc/shared';
import { Button, Dialog, Textarea } from '../ui';

const TITLE: Record<NudgeReason, string> = {
  not_rsvpd: "Remind everyone who hasn't replied",
  unpaid: 'Send a payment reminder',
  no_wine: 'Ask for their wine',
  no_label: 'Ask for a label photo',
};

/**
 * Confirm a nudge before it goes: who it reaches, and an optional line from
 * the host above the standard reminder copy.
 */
export function NudgeDialog({
  reason,
  names,
  totalCount,
  isTest,
  sending,
  onClose,
  onSend,
}: {
  /** null = closed. */
  reason: NudgeReason | null;
  /** The ticked people this reaches; empty when it goes to everyone in that state. */
  names: string[];
  /** Headcount when it isn't a named list (the "hasn't replied" count). */
  totalCount?: number;
  isTest: boolean;
  sending: boolean;
  onClose: () => void;
  onSend: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (reason) setNote('');
  }, [reason]);

  const shown = names.slice(0, 8);
  const more = names.length - shown.length;
  const count = names.length || totalCount || 0;

  return (
    <Dialog
      open={!!reason}
      onClose={onClose}
      title={reason ? TITLE[reason] : ''}
      description={`Goes to ${count} ${count === 1 ? 'person' : 'people'} by push and email, using the usual reminder wording.`}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={sending} onClick={() => onSend(note.trim())}>
            Send reminder
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {shown.length > 0 && (
          <p className="text-sm text-cream-100">
            {shown.join(', ')}
            {more > 0 && <span className="text-cream-300/60"> and {more} more</span>}
          </p>
        )}
        {isTest && (
          <p className="rounded border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-cream-200">
            This is a practice tasting: only the testers on its roster will receive it.
          </p>
        )}
        <Textarea
          label="A line from you (optional)"
          hint="Shown above the standard reminder."
          maxLength={1000}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Dialog>
  );
}
